import { memoriesOf, memorySortTime } from 'helpers/memories';
import { albumAccess, vaultAccess } from 'helpers/sharing';
import { Spinner } from 'common/spinner';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'hooks/use-media-query';
import { useLocation, useParams } from 'react-router-dom';
import { goToPage, RouteName } from 'routes';
import {
  albumMemoryBreadcrumb,
  memoriesBreadcrumb,
} from 'helpers/vault-breadcrumbs';
import { useBreadcrumbOrigin } from 'hooks/use-breadcrumb-origin';
import {
  AlbumContentSerialized,
  AlbumDetailType,
} from 'networking/types/album-detail';
import { adaptContentType, classnames } from 'helpers/utils';
import {
  albumOrderRequest,
  byAlbumOrder,
  byIdOrder,
  hasAlbumOrder,
} from 'helpers/memory-order';
import { AlbumContentType, Breakpoints, NotificationType } from 'common/enums';
import { NotificationObject } from 'models/notificationObject';
import { AppContext, appActions } from 'context/app-context';
import ViewModeEmptyStateImage from 'assets/images/view-mode-empty-state.png';
import { GuestRegistrationModal } from 'common/guest-registration-modal';
import { VaultTopBar } from 'pages/vault-detail/components/vault-top-bar';
import { AlbumSubHeader } from './components/album-sub-header';
import { AlbumEntryList } from './components/album-entry-list';
import { Controls } from './components/controls';
import {
  DetailsPanel,
  MemoryTags,
  PublicDetailsPanel,
} from './components/details-panel';
import { MemoryPager } from './components/memory-pager';
import { MemoryTitle } from './components/memory-title';
import { PublicTopBar } from './components/public-top-bar';
import { RenderContent } from './components/render-content';
import { useHorizontalSwipe } from './use-horizontal-swipe';
import screenStyles from './album-view-mode.module.scss';

interface AlbumViewModeProps {
  // When true, the screen shows every memory in the vault (the "Memories"
  // view) instead of a single album's contents.
  allMemories?: boolean;
}

// The view mode, in two versions with the same layout:
// - the owner's screen (/vaults/:id/album/:albumId/view and
//   /vaults/:id/memories/view): the member's full data, editable;
// - the public view (/view-mode/album/:albumId, /view-mode/content/:contentId
//   and /view-mode/vault/:vaultId), for shared links and QR codes: only the
//   public memories, whoever is signed in, and read-only.
const AlbumViewMode: React.FC<AlbumViewModeProps> = ({
  allMemories = false,
}) => {
  const { id, albumId, contentId, vaultId } = useParams<{
    id?: string;
    albumId?: string;
    contentId?: string;
    vaultId?: string;
  }>();
  const location = useLocation();
  // Where the user came from (dashboard, Albums or Memories tab). Only the
  // owner screen shows a breadcrumb, so it's unused for public routes.
  const breadcrumbOrigin = useBreadcrumbOrigin();
  const urlVaultId = id;
  const urlAlbumId = albumId;
  const urlContentId = contentId;
  // /view-mode/vault/:vaultId: the vault's public memories.
  const publicVaultId = vaultId;
  const [album, setAlbum] = useState<AlbumDetailType | null>(null);
  // When the screen lists memories from several albums (the Memories view,
  // the public vault view), this maps a content id to its album.
  const [contentAlbumMap, setContentAlbumMap] = useState<
    Record<number, AlbumDetailType>
  >({});
  const [fetching, setFetching] = useState(true);
  const [publicVaultTitle, setPublicVaultTitle] = useState('');
  const [allContents, setAllContents] = useState<AlbumContentSerialized[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [content, setContent] = useState<AlbumContentSerialized | undefined>(
    undefined,
  );
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [autoAdvanceCheckedOptions, setAutoAdvanceCheckedOptions] = useState<{
    [key in AlbumContentType]: boolean;
  }>({
    [AlbumContentType.image]: true,
    [AlbumContentType.video]: true,
    [AlbumContentType.audio]: true,
    [AlbumContentType.write]: true,
    [AlbumContentType.document]: true,
  });
  const { state, dispatch } = useContext(AppContext);
  const isPhone = useMediaQuery(`(max-width: ${Breakpoints.sm}px)`);

  // Owner (vault-scoped) screen vs the public read-only view.
  const isOwnerScreen = !!urlVaultId && state.logged;
  const { currentVault } = state;

  // The album that owns the currently-selected memory: looked up per content
  // when the screen mixes albums, otherwise just `album`.
  const currentAlbum = (content && contentAlbumMap[content.id]) || album;

  const handleAutoAdvanceCheckedOptionChange = (
    contentType: AlbumContentType,
  ) => {
    setAutoAdvanceCheckedOptions({
      ...autoAdvanceCheckedOptions,
      [contentType]: !autoAdvanceCheckedOptions[contentType],
    });
  };

  const selectInitialContent = (contents: AlbumContentSerialized[]) => {
    const requestedId = new URLSearchParams(location.search).get('content');
    const initial =
      (requestedId && contents.find((c) => c.id === Number(requestedId))) ||
      contents[0];
    if (initial) {
      setContent(initial);
      setCurrentIndex(contents.findIndex((c) => c.id === initial.id));
    }
  };

  const applyFetchedAlbum = (fetchedAlbum: AlbumDetailType) => {
    setAlbum(fetchedAlbum);
    // Newest first, like the Memories view; in the album's custom order once
    // it has been arranged.
    const memories = memoriesOf(fetchedAlbum).sort(
      (a, b) => memorySortTime(b) - memorySortTime(a),
    );
    const albumContents = hasAlbumOrder(memories)
      ? byAlbumOrder(memories)
      : memories;
    setAllContents(albumContents);
    if (albumContents.length > 0) selectInitialContent(albumContents);
  };

  // Every memory of the given albums, newest first, remembering each one's
  // album (the Memories view and the public vault view). With the vault's
  // custom order, in that order instead.
  const applyAlbumsMemories = (
    albums: AlbumDetailType[],
    memoryOrder: number[] = [],
  ) => {
    const map: Record<number, AlbumDetailType> = {};
    const contents: AlbumContentSerialized[] = [];
    albums.forEach((a) => {
      memoriesOf(a).forEach((c) => {
        map[c.id] = a;
        contents.push(c);
      });
    });
    contents.sort((a, b) => memorySortTime(b) - memorySortTime(a));
    const ordered = memoryOrder.length
      ? byIdOrder(contents, memoryOrder)
      : contents;
    setContentAlbumMap(map);
    setAllContents(ordered);
    setAlbum(null);
    if (ordered.length > 0) selectInitialContent(ordered);
  };

  const fetchAlbum = async () => {
    try {
      setFetching(true);

      // Owner (vault-scoped) screen: the album from its own endpoint, plus the
      // vault in summary form (no memories) for the top bar and breadcrumb.
      if (urlVaultId && state.logged) {
        const [fetchedAlbum, vault] = await Promise.all([
          VaultsController.getAlbum(Number(urlAlbumId)),
          VaultsController.getVault(Number(urlVaultId), 'summary'),
        ]);
        dispatch({ type: appActions.SET_CURRENT_VAULT, currentVault: vault });
        applyFetchedAlbum(fetchedAlbum);
        return;
      }

      // Public view: the album's public memories, even for a member. The
      // link's address is its slug (or an old numeric id).
      applyFetchedAlbum(await VaultsController.getSharedAlbum(urlAlbumId!));
    } catch (error) {
      goToPage(RouteName.NotFoundRedirect);
    } finally {
      setFetching(false);
    }
  };

  const fetchAllMemories = async () => {
    try {
      setFetching(true);
      const vault = await VaultsController.getVault(Number(urlVaultId), true);
      dispatch({ type: appActions.SET_CURRENT_VAULT, currentVault: vault });
      applyAlbumsMemories(
        [
          ...(vault.albums || []),
          ...(vault.unansweredAlbums || []),
        ] as unknown as AlbumDetailType[],
        vault.memoryOrder,
      );
    } catch (error) {
      goToPage(RouteName.NotFoundRedirect);
    } finally {
      setFetching(false);
    }
  };

  // The public vault view: every public memory in the vault, newest first.
  const fetchPublicVault = async () => {
    try {
      setFetching(true);
      const vault = await VaultsController.getSharedVault(publicVaultId!);
      setPublicVaultTitle(vault.title);
      applyAlbumsMemories([
        ...(vault.albums || []),
        ...(vault.unansweredAlbums || []),
      ] as unknown as AlbumDetailType[]);
    } catch (error) {
      goToPage(RouteName.NotFoundRedirect);
    } finally {
      setFetching(false);
    }
  };

  const fetchContent = async () => {
    try {
      setFetching(true);
      const fetchedContent = await VaultsController.getSharedContent(
        urlContentId!,
      );

      setAllContents([fetchedContent]);
      setContent(fetchedContent);
      setCurrentIndex(0);
      setAlbum(null);
    } catch (error) {
      goToPage(RouteName.NotFoundRedirect);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    // Public addresses are a link slug or an old numeric id.
    if (urlContentId) {
      fetchContent();
    } else if (allMemories && urlVaultId && Number(urlVaultId) > 0) {
      fetchAllMemories();
    } else if (publicVaultId) {
      fetchPublicVault();
    } else if (urlAlbumId) {
      fetchAlbum();
    }
  }, [
    urlAlbumId,
    urlContentId,
    urlVaultId,
    publicVaultId,
    allMemories,
    state.logged,
  ]);

  // Contributor links (?token=) open the public album or vault page with a
  // sign-up box for the new contributor.
  const handleRegister = ({
    email,
    name,
    token,
  }: {
    email: string;
    name: string;
    token: string;
  }) =>
    publicVaultId
      ? VaultsController.guestSelfRegister({
          guestInviteToken: token,
          email,
          name,
        })
      : VaultsController.albumGuestSelfRegister({
          guestInviteToken: token,
          email,
          name,
        });

  const nextContent = () => {
    if (currentIndex < allContents.length - 1) {
      let newIndex = currentIndex + 1;
      if (autoAdvance) {
        const oldIndex = newIndex - 1;
        while (
          newIndex < allContents.length &&
          !autoAdvanceCheckedOptions[
            adaptContentType(allContents[newIndex]) as AlbumContentType
          ]
        ) {
          newIndex += 1;
        }
        if (newIndex === allContents.length) {
          newIndex = oldIndex;
        }
      }

      setCurrentIndex(newIndex);
      setContent(allContents[newIndex]);
    }
  };

  const prevContent = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setContent(allContents[newIndex]);
    }
  };

  const selectContent = (selected: AlbumContentSerialized) => {
    setContent(selected);
    const newIndex = allContents.findIndex((c) => c.id === selected.id);
    if (newIndex !== -1) setCurrentIndex(newIndex);
  };

  const handleContentDeleted = (deletedId: number) => {
    const idx = allContents.findIndex((c) => c.id === deletedId);
    const next = allContents.filter((c) => c.id !== deletedId);
    setAllContents(next);
    setAlbum((prev) =>
      prev
        ? { ...prev, contents: prev.contents.filter((c) => c.id !== deletedId) }
        : prev,
    );
    if (content?.id === deletedId) {
      if (next.length === 0) {
        setContent(undefined);
        setCurrentIndex(0);
      } else {
        const newIdx = Math.min(idx, next.length - 1);
        setContent(next[newIdx]);
        setCurrentIndex(newIdx);
      }
    } else {
      const sel = next.findIndex((c) => c.id === content?.id);
      if (sel >= 0) setCurrentIndex(sel);
    }
  };

  const applyContentPatch = (
    entryId: number,
    partial: Partial<AlbumContentSerialized>,
  ) => {
    setAllContents((prev) =>
      prev.map((c) => (c.id === entryId ? { ...c, ...partial } : c)),
    );
    setAlbum((prev) =>
      prev
        ? {
            ...prev,
            contents: prev.contents.map((c) =>
              c.id === entryId ? { ...c, ...partial } : c,
            ),
          }
        : prev,
    );
    setContent((prev) =>
      prev && prev.id === entryId ? { ...prev, ...partial } : prev,
    );
  };

  const handleContentRenamed = (entryId: number, name: string) =>
    applyContentPatch(entryId, { name });

  // The vault's owner and collaborators may arrange the list (the API checks
  // the same).
  const { user } = state.data;
  const canReorder =
    isOwnerScreen &&
    !!currentVault &&
    !!user &&
    user.id > 0 &&
    (album
      ? albumAccess(album, vaultAccess(user, currentVault)).canReorder
      : vaultAccess(user, currentVault).fullAccess);

  const keepSelection = (list: AlbumContentSerialized[]) => {
    const selected = list.findIndex((c) => c.id === content?.id);
    if (selected >= 0) setCurrentIndex(selected);
  };

  // Each drop shows at once and is saved: "All memories" as the vault's own
  // order, an album as its memories' places. A failed save puts it back.
  const handleReorder = async (reordered: AlbumContentSerialized[]) => {
    const previous = allContents;
    setAllContents(reordered);
    keepSelection(reordered);
    const ids = reordered.map((c) => c.id);
    try {
      if (allMemories) {
        await VaultsController.updateVaultMemoryOrder(Number(urlVaultId), ids);
        if (currentVault) {
          dispatch({
            type: appActions.SET_CURRENT_VAULT,
            currentVault: { ...currentVault, memoryOrder: ids },
          });
        }
      } else {
        const request = albumOrderRequest(ids);
        await VaultsController.updateContentOrder(request);
        const placeOf = new Map(
          request.map((item) => [item.contentId, Number(item.order)]),
        );
        setAlbum((prev) =>
          prev
            ? {
                ...prev,
                contents: prev.contents.map((c) =>
                  placeOf.has(c.id) ? { ...c, order: placeOf.get(c.id) } : c,
                ),
              }
            : prev,
        );
      }
    } catch (error) {
      setAllContents(previous);
      keepSelection(previous);
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'The new order could not be saved',
          type: NotificationType.Error,
        }),
      });
    }
  };

  const hasContents = allContents.length > 0;

  // Phones: moving on stops what's playing, as in the mobile app, instead of
  // starting the next video without a tap.
  const phonePrev = () => {
    setIsPlaying(false);
    prevContent();
  };
  const phoneNext = () => {
    setIsPlaying(false);
    nextContent();
  };
  const swipe = useHorizontalSwipe(phoneNext, phonePrev);

  // Phones: each memory opens at its top.
  useEffect(() => {
    if (isPhone) window.scrollTo(0, 0);
  }, [content?.id, isPhone]);

  // The memory itself is never a crumb (its title is in the sub-header).
  const breadcrumb = allMemories
    ? memoriesBreadcrumb(Number(urlVaultId), breadcrumbOrigin)
    : albumMemoryBreadcrumb({
        vaultId: Number(urlVaultId),
        albumId: Number(urlAlbumId),
        albumName: currentAlbum?.name || 'Album',
        origin: breadcrumbOrigin,
      });

  // The selected memory and the player controls, in the center column or
  // fullscreen.
  const renderMedia = () => (
    <>
      <div
        className={
          isFullScreen ? screenStyles.fullScreenMedia : screenStyles.mediaArea
        }
      >
        {content && (
          <RenderContent
            content={content}
            isFullScreen={isFullScreen}
            isPlaying={isPlaying}
            setIsPlaying={setIsPlaying}
            onFinish={autoAdvance ? nextContent : undefined}
          />
        )}
      </div>
      {content && (
        <Controls
          isFullScreen={isFullScreen}
          setIsFullScreen={setIsFullScreen}
          prevContent={prevContent}
          nextContent={nextContent}
          currentIndex={currentIndex}
          allContents={allContents}
          currentContent={content}
          isPlaying={isPlaying}
          setIsPlaying={setIsPlaying}
          autoAdvance={autoAdvance}
          setAutoAdvance={setAutoAdvance}
          autoAdvanceCheckedOptions={autoAdvanceCheckedOptions}
          handleAutoAdvanceCheckedOptionChange={
            handleAutoAdvanceCheckedOptionChange
          }
        />
      )}
    </>
  );

  const emptyStateTitle = () => {
    if (publicVaultId) return 'This Vault has no memories to show yet.';
    if (isOwnerScreen) return 'This Album has no content yet.';
    return 'This Album has no memories to show yet.';
  };

  const renderEmptyState = () => (
    <div
      className={classnames(
        screenStyles.emptyStateContainer,
        'text__body__regular__large__gray700',
      )}
    >
      <img src={ViewModeEmptyStateImage} alt="View mode empty state" />
      <div className={screenStyles.emptyStateText}>
        {emptyStateTitle()}
        <span className="text__body__regular__small__gray500">
          Once content is added, you&apos;ll be able to view it here.
        </span>
      </div>
    </div>
  );

  if (fetching) return <Spinner />;

  // Fullscreen player (owner screen and public view).
  if (isFullScreen) {
    return (
      <div className={screenStyles.fullScreenContainer}>
        <div className={screenStyles.fullScreenContent}>
          {hasContents ? renderMedia() : renderEmptyState()}
        </div>
      </div>
    );
  }

  // Public view: the memory's album, when the page mixes several albums
  // (the hidden default one isn't named).
  const publicAlbumName =
    publicVaultId && currentAlbum && !currentAlbum.isDefault
      ? currentAlbum.name
      : null;

  // Contributor links (?token=) show it; a single shared memory doesn't.
  const guestRegistration = !urlContentId && (
    <GuestRegistrationModal
      invitationText={
        publicVaultId
          ? 'invited you to contribute to their Vault.'
          : 'invited you to contribute to their Album.'
      }
      successMessage={
        publicVaultId
          ? 'You can now contribute to this vault'
          : 'You can now contribute to this album'
      }
      onRegister={handleRegister}
    />
  );

  // Phones: the mobile app's memory screen, straight away (no list to pick
  // from). Under the given top, the memory itself (title, location and date,
  // media, details) and the pager at the bottom; swiping moves between
  // memories too. The owner can change it all; the public view only shows
  // what's set.
  const renderPhoneScreen = (
    top: React.ReactNode,
    editable: boolean,
    pagerTitle: string,
  ) => {
    const type = content ? adaptContentType(content) : undefined;
    const showPager = allContents.length > 1;
    // On the member's screen, only what their role lets them change.
    const canChange = editable && (content?.myAccess?.canEdit ?? true);
    const media = content && (
      <RenderContent
        content={content}
        isFullScreen={false}
        isPlaying={isPlaying}
        setIsPlaying={setIsPlaying}
        onFinish={autoAdvance ? nextContent : undefined}
      />
    );
    return (
      <div
        className={classnames(screenStyles.phoneScreen, {
          [screenStyles.withPager]: showPager,
          [screenStyles.phonePublic]: !editable,
        })}
      >
        {top}
        {content ? (
          <div
            className={screenStyles.phonePage}
            onTouchStart={swipe.onTouchStart}
            onTouchEnd={swipe.onTouchEnd}
          >
            <div className={screenStyles.phoneTitleBlock}>
              <MemoryTitle
                variant="page"
                content={content}
                editable={canChange}
                onRenamed={handleContentRenamed}
              />
              <MemoryTags
                variant="page"
                readOnly={!canChange}
                content={content}
                onContentUpdated={applyContentPatch}
              />
            </div>
            <div className={screenStyles.phoneMediaWrapper}>
              {/* A photo opens full screen; the rest play right here. */}
              {type === AlbumContentType.image ? (
                <button
                  type="button"
                  className={classnames(
                    screenStyles.phoneMedia,
                    screenStyles.phonePhoto,
                  )}
                  aria-label="View full screen"
                  onClick={() => setIsFullScreen(true)}
                >
                  {media}
                </button>
              ) : (
                <div
                  className={classnames(screenStyles.phoneMedia, {
                    [screenStyles.phoneVideo]: type === AlbumContentType.video,
                    [screenStyles.phoneAudio]: type === AlbumContentType.audio,
                    [screenStyles.phoneDocument]:
                      type === AlbumContentType.document,
                    [screenStyles.phoneNote]: type === AlbumContentType.write,
                  })}
                  // The audio's seek bar and the PDF's pages take the swipe.
                  data-no-swipe={
                    type === AlbumContentType.audio ||
                    type === AlbumContentType.document
                      ? ''
                      : undefined
                  }
                >
                  {media}
                </div>
              )}
            </div>
            {editable && currentAlbum && (
              <DetailsPanel
                phone
                album={currentAlbum}
                content={content}
                onContentUpdated={applyContentPatch}
              />
            )}
            {!editable && (
              <PublicDetailsPanel
                phone
                content={content}
                albumName={publicAlbumName}
              />
            )}
          </div>
        ) : (
          renderEmptyState()
        )}
        {showPager && (
          <MemoryPager
            index={currentIndex}
            total={allContents.length}
            title={pagerTitle}
            onPrev={phonePrev}
            onNext={phoneNext}
          />
        )}
      </div>
    );
  };

  if (isPhone && isOwnerScreen) {
    return renderPhoneScreen(
      <AlbumSubHeader
        phone
        album={allMemories ? null : currentAlbum}
        content={content}
        vaultId={Number(urlVaultId)}
        isGuest={false}
        editable={content?.myAccess?.canEdit ?? true}
        onContentUpdated={applyContentPatch}
        onDeleted={handleContentDeleted}
        onFullScreen={() => setIsFullScreen(true)}
      />,
      true,
      allMemories ? 'All memories' : (currentAlbum?.name ?? ''),
    );
  }

  if (isPhone) {
    const title = publicVaultId ? publicVaultTitle : (album?.name ?? '');
    return renderPhoneScreen(
      <>
        {guestRegistration}
        <PublicTopBar title={title} />
      </>,
      false,
      title,
    );
  }

  // Owner, vault-scoped, full editing screen.
  if (isOwnerScreen) {
    return (
      <div className={screenStyles.screen}>
        {currentVault && (
          <VaultTopBar vault={currentVault} breadcrumb={breadcrumb} />
        )}
        <AlbumSubHeader
          album={allMemories ? null : currentAlbum}
          content={content}
          vaultId={Number(urlVaultId)}
          isGuest={false}
          editable={content?.myAccess?.canEdit ?? true}
          onRenamed={handleContentRenamed}
          onContentUpdated={applyContentPatch}
          onDeleted={handleContentDeleted}
        />
        <div className={screenStyles.columns}>
          <div className={screenStyles.leftCol}>
            <AlbumEntryList
              contents={allContents}
              activeContentId={content?.id}
              onSelect={selectContent}
              onReorder={canReorder ? handleReorder : undefined}
            />
          </div>
          <div className={screenStyles.centerCol}>
            {hasContents ? renderMedia() : renderEmptyState()}
          </div>
          <div className={screenStyles.rightCol}>
            {currentAlbum && (
              <DetailsPanel
                album={currentAlbum}
                content={content}
                onContentUpdated={applyContentPatch}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Public view: the same layout, read-only. A single shared memory has no
  // list to pick from.
  return (
    <div className={screenStyles.screen}>
      {guestRegistration}
      <PublicTopBar title={publicVaultId ? publicVaultTitle : album?.name} />
      <AlbumSubHeader album={null} content={content} isGuest publicView />
      <div className={screenStyles.columns}>
        {!urlContentId && (
          <div className={screenStyles.leftCol}>
            <AlbumEntryList
              contents={allContents}
              activeContentId={content?.id}
              onSelect={selectContent}
            />
          </div>
        )}
        <div className={screenStyles.centerCol}>
          {hasContents ? renderMedia() : renderEmptyState()}
        </div>
        <div className={screenStyles.rightCol}>
          <PublicDetailsPanel content={content} albumName={publicAlbumName} />
        </div>
      </div>
    </div>
  );
};

export { AlbumViewMode };
